// eslint-disable-next-line import/no-unresolved
import { useAuth } from "@src/hooks/useAuth";
import { callApi as callApiForSettingsUpdate } from "@src/api";
import { parseJSON } from "../../../../../utility/CustomFunction";
import { BILLING_SERVER_URL, callApi } from "../utils/apiCaller";

const useBillingApi = () => {
  const { selectedBusiness } = useAuth();

  const prefix = `/${selectedBusiness?.id}/billing`;

  const SUBSCRIPTIONS = {
    getAll: (uid) =>
      callApi("get", `${prefix}?uid=${uid}&dest=api/subscription/:UID:/all`),
    addAdjustment: (values) =>
      callApi(
        "post",
        `${prefix}?dest=api/subscription/:UID:/add-adjustment`,
        values
      ),
    create: (values) =>
      callApi("post", `${prefix}?dest=api/subscription/:UID:/create`, values),
    changeStatus: (values) =>
      callApi(
        "post",
        `${prefix}?dest=api/subscription/:UID:/change-status`,
        values
      ),
    removeStatus: (values) =>
      callApi(
        "post",
        `${prefix}?dest=api/subscription/:UID:/remove-status`,
        values
      ),
    getCount: (uid) =>
      callApi("get", `${prefix}?uid=${uid}&dest=api/subscription/:UID:/count`),
    getCounts: () =>
      callApi("get", `${prefix}?dest=api/subscription/:UID:/counts`),
    deleteSub: (data) =>
      callApi(
        "post",
        `${prefix}?dest=api/subscription/:UID:/soft-delete`,
        data
      ),
  };

  const PLANS = {
    getAll: () => callApi("get", `${prefix}?dest=api/plan/all`),
  };

  const REPORTS = {
    getBusinessReports: () =>
      callApi("get", `${prefix}?dest=api/reports/:UID:/businesses`),
    getInvoceReportsOfBusiness: (bid, status) =>
      callApi(
        "get",
        `${prefix}?dest=${encodeURIComponent(
          `api/reports/:UID:/invoices/${bid}${
            status ? `?status=${status}` : ""
          }`
        )}
    `
      ),
    getRecurringInvoiceReports: (obj) =>
      callApi(
        "get",
        `${prefix}?dest=${encodeURIComponent(
          `api/reports/:UID:/recurring-invoices?${new URLSearchParams(
            parseJSON(JSON.stringify(obj), true)
          ).toString()}`
        )}`
      ),
    getOnetimeInvoiceReports: ({ status }) =>
      callApi(
        "get",
        `${prefix}?dest=${encodeURIComponent(
          `api/reports/:UID:/onetime-invoices${
            status ? `?status=${status}` : ""
          }`
        )}`
      ),
  };

  const BUSINESS = {
    getAllBusinesses: () =>
      callApi("get", `${prefix}?dest=api/businesses/:UID:/all`),
  };

  const ACCOUNT_BALANCE = {
    getAll: (uid) =>
      callApi("get", `${prefix}?uid=${uid}&dest=api/accountbalance/:UID:/all`),
    create: (values) =>
      callApi("post", `${prefix}?dest=api/accountbalance/:UID:/create`, values),
    update: (values) =>
      callApi("post", `${prefix}?dest=api/accountbalance/:UID:/update`, values),
    delete: (values) =>
      callApi("post", `${prefix}?dest=api/accountbalance/:UID:/delete`, values),
    getFundBalance: (uid) =>
      callApi(
        "get",
        `${prefix}?uid=${uid}&dest=api/accountbalance/:UID:/all/fund`
      ),
    getMessageBalance: (uid) =>
      callApi(
        "get",
        `${prefix}?uid=${uid}&dest=api/accountbalance/:UID:/all/message`
      ),
    getAccountBalanceLogs: (bid) =>
      callApi(
        "get",
        `${prefix}?dest=api/accountbalance/:UID:/business/${bid}/history/get`
      ),
  };

  const PAYMENT_METHOD = {
    getAll: (uid) =>
      callApi("get", `${prefix}?uid=${uid}&dest=api/payment/:UID:/all`),
    createSetupIntent: (uid) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/payment/:UID:/create-setup-intent`
      ),
    create: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/payment/:UID:/create`,
        values
      ),
    updateName: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/payment/:UID:/update-name`,
        values
      ),
    updateCard: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/payment/:UID:/update-card`,
        values
      ),
    makeDefault: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/payment/:UID:/make-default`,
        values
      ),
    makeDefaultByUser: (uid, values) =>
      callApi(
        "post",
        `/${uid}/billing?uid=${uid}&dest=api/payment/:UID:/make-default`,
        values,
        {
          isUser: true,
        }
      ),
    remove: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/payment/:UID:/remove`,
        values
      ),
    addToSub: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/payment/:UID:/add-to-sub`,
        values
      ),
    addToMultipleSub: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/payment/:UID:/add-to-multiple-subs`,
        values
      ),
    removeFromSub: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/payment/:UID:/remove-from-sub`,
        values
      ),
    createLinkToken: (uid) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/payment/:UID:/create_link_token`
      ),
    getAllByUser: (uid) =>
      // callApi("get", `/${uid}/billing?dest=api/payment/:UID:/all`, undefined, {
      callApi(
        "get",
        `/${uid}/billing?uid=${uid}&dest=api/payment/:UID:/all`,
        undefined,
        {
          isUser: true,
        }
      ),
    createByUser: (uid, values) =>
      callApi("post", `/${uid}/billing?dest=api/payment/:UID:/create`, values, {
        isUser: true,
      }),
    createSetupIntentByUser: (uid) =>
      callApi(
        "post",
        `/${uid}/billing?dest=api/payment/:UID:/create-setup-intent`,
        undefined,
        { isUser: true }
      ),
    createLinkTokenByUser: (uid) =>
      callApi(
        "post",
        `/${uid}/billing?dest=api/payment/:UID:/create_link_token`,
        undefined,
        { isUser: true }
      ),
    updateNameByUser: (uid, values) =>
      callApi(
        "post",
        `/${uid}/billing?dest=api/payment/:UID:/update-name`,
        values,
        {
          isUser: true,
        }
      ),
  };

  const ONE_TIME_BILLING = {
    charge: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/onetimebilling/:UID:/charge`,
        values
      ),
    chargeByUser: (uid, values) =>
      callApi(
        "post",
        `/${uid}/billing?dest=api/onetimebilling/:UID:/charge`,
        values,
        {
          isUser: true,
        }
      ),
    createInInstallments: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/onetimebilling/:UID:/charge-installment`,
        values
      ),
    createInInstallmentsByUser: (uid, values) =>
      callApi(
        "post",
        `/${uid}/billing?dest=api/onetimebilling/:UID:/charge-installment`,
        values,
        {
          isUser: true,
        }
      ),
    chargeAccountBalance: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/onetimebilling/:UID:/charge-account-balance`,
        values
      ),
    chargeInvoice: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/onetimebilling/:UID:/charge-invoice`,
        values
      ),
    updateAccountBalanceLogs: async (data) => {
      const resp = await callApiForSettingsUpdate(
        `/protected/business/${
          selectedBusiness?.id
        }/iorequest?dest=${encodeURIComponent(
          `:BID:/campaigns/updateaccountbalancelogs`
        )}`,
        "POST",
        data
      );
      return resp;
    },
  };

  const INVOICE = {
    getAll: (uid, { startDate, endDate }) =>
      callApi(
        "get",
        `${prefix}?uid=${uid}&dest=${encodeURIComponent(
          `api/invoice/:UID:/all${
            startDate && endDate
              ? `?startdate=${startDate}&enddate=${endDate}`
              : ""
          }`
        )}`
      ),
    getInvoiceDetail: (uid, invoiceId) =>
      callApi(
        "get",
        `${prefix}?uid=${uid}&dest=api/invoice/:UID:/${invoiceId}/detail/get`
      ),
    getFilteredInvoices: (uid, filters) =>
      callApi(
        "get",
        `${prefix}?uid=${uid}&dest=${encodeURIComponent(
          `api/invoice/:UID:/withfilter${
            filters ? `?s=${JSON.stringify(filters)}` : ""
          }`
        )}`
      ),
    getOneTime: (uid, { status }) =>
      callApi(
        "get",
        `${prefix}?uid=${uid}&dest=${encodeURIComponent(
          `api/invoice/:UID:/onetime${status ? `?status=${status}` : ""}`
        )}`
      ),
    getAllPayments: (uid, invoiceId) =>
      callApi(
        "get",
        `${prefix}?uid=${uid}&dest=${encodeURIComponent(
          `api/invoice/:UID:/payments/${invoiceId}/get`
        )}`
      ),
    changePaymentMethod: (uid, values) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/invoice/:UID:/change-payment-method`,
        values
      ),
    changeStatus: (values) =>
      callApi(
        "post",
        `${prefix}?dest=api/invoice/:UID:/change-invoice-status`,
        values
      ),
    changeDueDate: (values) =>
      callApi(
        "post",
        `${prefix}?dest=api/invoice/:UID:/change-due-date`,
        values
      ),
    updateComments: (values) =>
      callApi(
        "post",
        `${prefix}?dest=api/invoice/:UID:/update-comment`,
        values
      ),
    resetFailedAttempts: (invoiceId) =>
      callApi(
        "post",
        `${prefix}?dest=api/invoice/:UID:/${invoiceId}/reset-attempts`,
        undefined
      ),
    removePaymentMethod: (uid, invoiceId) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=api/invoice/:UID:/remove-payment-method`,
        { invoiceId }
      ),
    // eslint-disable-next-line camelcase
    refundInvoice: (uid, invoice_id, foreign_payment_id, amount) =>
      callApi(
        "post",
        `${prefix}?uid=${uid}&dest=${encodeURIComponent(
          `api/invoice/:UID:/refund`
        )}`,
        { invoice_id, foreign_payment_id, amount }
      ),
    deleteInvoice: (data) =>
      callApi("post", `${prefix}?dest=api/invoice/:UID:/soft-delete`, data),
    pdf: (uid, invoiceId) =>
      `${window.location.protocol}//${window.location.hostname}/growthzilla/remote/public${BILLING_SERVER_URL}${prefix}?uid=${uid}&dest=api/invoice/:UID:/pdf/${invoiceId}`,
  };

  const SCHEDULER = {
    trigger: () => callApi("post", `${prefix}?dest=api/trigger-scheduler`),
  };

  const BUSINESS_SETTINGS = {
    update: async (bid, settings) => {
      // console.log("test", process.env);
      const res = await callApiForSettingsUpdate(
        `/protected/business/${bid}/bizsettings/update`,
        "POST",
        {
          value: JSON.stringify(settings),
          id: process.env.REACT_APP_TOP_UP_PREFERENCE_SETTINGS_ID,
        }
      );

      return res && res.status === true ? res.data : {};
    },
  };

  return {
    SUBSCRIPTIONS,
    PLANS,
    REPORTS,
    BUSINESS,
    ACCOUNT_BALANCE,
    PAYMENT_METHOD,
    ONE_TIME_BILLING,
    INVOICE,
    SCHEDULER,
    BUSINESS_SETTINGS,
  };
};

export default useBillingApi;
